import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Settings2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { apiFetch } from '../services/apiClient';
import {
  getConfigSchema, splitArgs, isConfigComplete,
  type MCPConfigField, type MCPConfigSchema,
} from '../lib/mcpConfigSchemas';

export interface MCPConfigDialogProps {
  open: boolean;
  onClose: () => void;
  server: { name: string; label?: string; source?: string } | null;
  /** 保存成功后回调（开启流程中触发时用于继续开启） */
  onSaved?: () => void;
  t?: any;
}

const ADAPTER_CONFIG_KEY = 'mcp_adapter_config';

export function MCPConfigDialog({ open, onClose, server, onSaved, t }: MCPConfigDialogProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [schema, setSchema] = useState<MCPConfigSchema | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});

  // 打开时加载现有配置
  useEffect(() => {
    if (!open || !server) return;
    const sc = getConfigSchema(server.name, server.source);
    setSchema(sc);
    setValues({});
    if (sc.fields.length === 0) return;

    setLoading(true);
    (async () => {
      try {
        if (sc.storage === 'settings') {
          const res = await apiFetch(`/api/settings/${ADAPTER_CONFIG_KEY}`);
          if (res.ok) {
            const all = await res.json();
            const mine = (all && typeof all === 'object' ? all[server.name] : null) || {};
            const filled: Record<string, string> = {};
            for (const f of sc.fields) filled[f.key] = mine[f.key] || '';
            setValues(filled);
          }
        } else {
          const res = await apiFetch('/api/mcp');
          if (res.ok) {
            const data = await res.json();
            const cfg = (data.servers || []).find((s: any) => s.name === server.name);
            if (cfg) {
              const filled: Record<string, string> = {};
              if (sc.argsMapping === 'paths' && sc.pathsField) {
                const { paths } = splitArgs(cfg.args || []);
                filled[sc.pathsField] = paths.join('\n');
              } else {
                filled.command = cfg.command || '';
                filled.args = (cfg.args || []).join(' ');
                filled.env = cfg.env ? JSON.stringify(cfg.env, null, 2) : '';
              }
              setValues(filled);
            }
          }
        }
      } catch { /* 加载失败时保持空表单 */ }
      setLoading(false);
    })();
  }, [open, server]);

  if (!open || !server || !schema) return null;

  const isZh = t?.langCode !== 'en';
  const displayName = server.label || server.name;

  const handleSave = async () => {
    if (!isConfigComplete(schema, values)) {
      toast.warning(t?.mcpConfigRequired || '请先完成必填项配置');
      return;
    }
    setSaving(true);
    try {
      if (schema.storage === 'settings') {
        // 读取-合并-写回，避免覆盖其他适配器的配置
        let all: Record<string, any> = {};
        try {
          const res = await apiFetch(`/api/settings/${ADAPTER_CONFIG_KEY}`);
          if (res.ok) {
            const val = await res.json();
            if (val && typeof val === 'object') all = val;
          }
        } catch { /* 首次保存时为空对象 */ }
        const res = await apiFetch('/api/settings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key: ADAPTER_CONFIG_KEY, value: { ...all, [server.name]: values } }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } else {
        let body: any;
        if (schema.argsMapping === 'paths' && schema.pathsField) {
          // 保留 args 包调用前缀，仅替换路径部分
          const res = await apiFetch('/api/mcp');
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          const cfg = (data.servers || []).find((s: any) => s.name === server.name);
          const { prefix } = splitArgs(cfg?.args || []);
          const paths = (values[schema.pathsField] || '')
            .split('\n').map(p => p.trim()).filter(Boolean);
          if (paths.length === 0) throw new Error(isZh ? '路径不能为空' : 'Path required');
          body = { args: [...prefix, ...paths] };
        } else {
          let env: Record<string, string> | undefined;
          const envText = (values.env || '').trim();
          if (envText) {
            try { env = JSON.parse(envText); } catch { throw new Error(isZh ? '环境变量 JSON 格式错误' : 'Invalid env JSON'); }
          }
          body = {
            command: (values.command || '').trim(),
            args: (values.args || '').split(/\s+/).filter(Boolean),
            ...(env ? { env } : {}),
          };
        }
        const res = await apiFetch(`/api/mcp/config/${encodeURIComponent(server.name)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      }
      toast.success(t?.mcpConfigSaved || '配置已保存');
      onClose();
      onSaved?.();
    } catch (err: any) {
      toast.error(`${t?.mcpUpdateFailed || '保存失败'}: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field: MCPConfigField) => {
    const val = values[field.key] || '';
    const setVal = (v: string) => setValues(prev => ({ ...prev, [field.key]: v }));
    return (
      <div key={field.key} className="space-y-1.5">
        <label className="text-xs font-bold text-white/60 flex items-center gap-1">
          {field.label}
          {field.required && <span className="text-red-400">*</span>}
        </label>
        {field.type === 'textarea' ? (
          <textarea
            value={val}
            onChange={e => setVal(e.target.value)}
            placeholder={field.placeholder}
            rows={4}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white/90 placeholder:text-white/25 focus:outline-none focus:border-celestial-saturn/50 resize-y font-mono"
          />
        ) : (
          <Input
            type={field.type === 'password' ? 'password' : 'text'}
            value={val}
            onChange={e => setVal(e.target.value)}
            placeholder={field.placeholder}
            className="bg-white/5 border-white/10 rounded-xl text-sm"
          />
        )}
        {field.hint && <p className="text-[11px] text-white/35">{field.hint}</p>}
      </div>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      {/* 遮罩 */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      {/* 弹窗 */}
      <div className="relative w-full max-w-md bg-gradient-to-br from-[#141419] to-[#0c0c10] rounded-3xl border border-white/10 shadow-2xl shadow-black/60 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div className="flex items-center gap-2.5">
            <Settings2 size={16} className="text-celestial-saturn" />
            <h3 className="text-sm font-bold text-white/90">
              {displayName} · {t?.mcpConfigTitle || '配置'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 hover:text-white/80 transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="animate-spin text-white/40" />
            </div>
          ) : schema.fields.length === 0 ? (
            <p className="text-sm text-white/45 text-center py-6">
              {t?.mcpConfigNoFields || '该服务无需配置，直接打开开关即可使用'}
            </p>
          ) : (
            schema.fields.map(renderField)
          )}
        </div>

        {schema.fields.length > 0 && !loading && (
          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-white/5">
            <Button onClick={onClose} variant="outline" className="text-xs px-4 h-9 rounded-xl bg-white/5 border-white/10">
              {t?.mcpConfigCancel || '取消'}
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="text-xs px-4 h-9 rounded-xl bg-celestial-saturn text-black font-bold hover:opacity-90"
            >
              {saving && <Loader2 size={12} className="animate-spin mr-1.5" />}
              {t?.mcpConfigSave || '保存配置'}
            </Button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
